/**
 * Random dates and times inside a range.
 *
 * Dates are calendar days in the proleptic Gregorian calendar, handled as whole-number
 * day counts since 1970-01-01 (the civil-date algorithms of Howard Hinnant), so no time
 * zone or daylight-saving rule can move a result by a day. Times are wall-clock minutes
 * from midnight on that day.
 *
 * The sampler never lists the candidate days. It counts the allowed weekdays in the range
 * with arithmetic, then maps a uniform index to the n-th allowed day, which keeps a
 * 9,000-year range as cheap as a 9-day one and makes every allowed day equally likely.
 */

import {
  randomBelow,
  sampleIndices,
  type Uint32Source,
} from "@/lib/tool-kit/random-fun/rng";

export const MAX_COUNT = 1000;
export const MIN_YEAR = 1;
export const MAX_YEAR = 9999;
const DAYS_PER_WEEK = 7;
const MS_PER_DAY = 86_400_000;
const MINUTES_PER_HOUR = 60;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK_TIME = /^(\d{2}):(\d{2})$/;

/** Weekday numbers follow `Date.getUTCDay`: 0 is Sunday, 6 is Saturday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export const ALL_WEEKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6];
export const WORK_WEEKDAYS: readonly Weekday[] = [1, 2, 3, 4, 5];
export const WEEKEND_WEEKDAYS: readonly Weekday[] = [0, 6];

/* --------------------------------------------------------------- civil dates */

/** Days since 1970-01-01 for a Gregorian year, month (1–12) and day. */
export function daysFromCivil(
  year: number,
  month: number,
  day: number
): number {
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yearOfEra = y - era * 400;
  const shifted = month > 2 ? month - 3 : month + 9;
  const dayOfYear = Math.floor((153 * shifted + 2) / 5) + day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

/** The Gregorian year, month and day of a day count. */
export function civilFromDays(days: number): CivilDate {
  const z = days + 719_468;
  const era = Math.floor(z / 146_097);
  const dayOfEra = z - era * 146_097;
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36_524) -
      Math.floor(dayOfEra / 146_096)) /
      365
  );
  const dayOfYear =
    dayOfEra -
    (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const shifted = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * shifted + 2) / 5) + 1;
  const month = shifted < 10 ? shifted + 3 : shifted - 9;
  const year = yearOfEra + era * 400 + (month <= 2 ? 1 : 0);
  return { year, month, day };
}

export function weekdayOf(days: number): Weekday {
  // 1970-01-01 was a Thursday (4).
  return ((((days + 4) % DAYS_PER_WEEK) + DAYS_PER_WEEK) %
    DAYS_PER_WEEK) as Weekday;
}

function daysInMonth(year: number, month: number): number {
  return daysFromCivil(year, month + 1, 1) - daysFromCivil(year, month, 1);
}

/** `YYYY-MM-DD` to a day count, or null when it is not a real date in years 1–9999. */
export function parseIsoDate(text: string): number | null {
  const match = ISO_DATE.exec(text.trim());
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < MIN_YEAR || year > MAX_YEAR || month < 1 || month > 12) {
    return null;
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    return null;
  }
  return daysFromCivil(year, month, day);
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}

export function toIsoDate(days: number): string {
  const { year, month, day } = civilFromDays(days);
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/** `HH:MM` to minutes after midnight, or null. */
export function parseClock(text: string): number | null {
  const match = CLOCK_TIME.exec(text.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * MINUTES_PER_HOUR + minutes;
}

export function toClock(minutes: number): string {
  return `${pad(Math.floor(minutes / MINUTES_PER_HOUR), 2)}:${pad(minutes % MINUTES_PER_HOUR, 2)}`;
}

/* ----------------------------------------------------------------- sampling */

export interface DateSpec {
  /** First day, as a day count. */
  start: number;
  /** Last day, as a day count, included. */
  end: number;
  weekdays: readonly Weekday[];
  /** Earliest minute of the day, or null for dates only. */
  fromMinute: number | null;
  toMinute: number | null;
  /** Gap between selectable times, in minutes. */
  stepMinutes: number;
}

/** The allowed days, as offsets (0–6) from `start` within one repeating week. */
function weekOffsets(start: number, weekdays: readonly Weekday[]): number[] {
  const first = weekdayOf(start);
  const offsets: number[] = [];
  for (let d = 0; d < DAYS_PER_WEEK; d += 1) {
    if (weekdays.includes(((first + d) % DAYS_PER_WEEK) as Weekday)) {
      offsets.push(d);
    }
  }
  return offsets;
}

/** How many days in the range fall on an allowed weekday. */
export function countAllowedDays(spec: DateSpec): number {
  const offsets = weekOffsets(spec.start, spec.weekdays);
  if (offsets.length === 0 || spec.end < spec.start) {
    return 0;
  }
  const span = spec.end - spec.start + 1;
  const fullWeeks = Math.floor(span / DAYS_PER_WEEK);
  const remainder = span % DAYS_PER_WEEK;
  return (
    fullWeeks * offsets.length + offsets.filter((o) => o < remainder).length
  );
}

/** The `index`-th allowed day (0-based, earliest first). */
export function allowedDayAt(spec: DateSpec, index: number): number {
  const offsets = weekOffsets(spec.start, spec.weekdays);
  const perWeek = offsets.length;
  const week = Math.floor(index / perWeek);
  return spec.start + week * DAYS_PER_WEEK + (offsets[index % perWeek] ?? 0);
}

/** How many selectable times a day has (1 when the spec is dates only). */
export function slotsPerDay(spec: DateSpec): number {
  if (spec.fromMinute === null || spec.toMinute === null) {
    return 1;
  }
  return Math.floor((spec.toMinute - spec.fromMinute) / spec.stepMinutes) + 1;
}

/** Every date (and time) the spec allows. */
export function countOutcomes(spec: DateSpec): number {
  return countAllowedDays(spec) * slotsPerDay(spec);
}

export interface DrawnDate {
  /** Day count since 1970-01-01. */
  day: number;
  /** Minute after midnight, or null for dates only. */
  minute: number | null;
}

export function outcomeAt(spec: DateSpec, index: number): DrawnDate {
  const slots = slotsPerDay(spec);
  const day = allowedDayAt(spec, Math.floor(index / slots));
  if (spec.fromMinute === null) {
    return { day, minute: null };
  }
  return { day, minute: spec.fromMinute + (index % slots) * spec.stepMinutes };
}

export type DateOrder = "random" | "ascending" | "descending";

export interface DrawOptions {
  count: number;
  unique: boolean;
  order: DateOrder;
}

/**
 * Draws dates (and times) uniformly from everything the spec allows.
 *
 * Outcome indices are ordered by date and then time, so sorting the indices sorts the
 * results. Returns null when a unique draw asks for more than the range holds.
 */
export function drawDates(
  spec: DateSpec,
  options: DrawOptions,
  source?: Uint32Source
): DrawnDate[] | null {
  const total = countOutcomes(spec);
  if (total === 0 || options.count < 1) {
    return [];
  }
  let indices: number[];
  if (options.unique) {
    if (options.count > total) {
      return null;
    }
    indices = sampleIndices(total, options.count, source);
  } else {
    indices = Array.from({ length: options.count }, () =>
      randomBelow(total, source)
    );
  }
  if (options.order === "ascending") {
    indices.sort((a, b) => a - b);
  } else if (options.order === "descending") {
    indices.sort((a, b) => b - a);
  }
  return indices.map((index) => outcomeAt(spec, index));
}

/** The date and time as milliseconds since the epoch, for `Intl.DateTimeFormat` in UTC. */
export function toEpochMs(drawn: DrawnDate): number {
  return drawn.day * MS_PER_DAY + (drawn.minute ?? 0) * 60_000;
}

/* ----------------------------------------------------------------- the form */

export interface DateForm {
  start: string;
  end: string;
  weekdays: readonly Weekday[];
  useTime: boolean;
  from: string;
  to: string;
  stepMinutes: number;
}

export type DateProblem =
  | "start"
  | "end"
  | "order"
  | "weekdays"
  | "from"
  | "to"
  | "timeOrder"
  | "empty";

/** Turns the form's text into a spec, or names the first thing that is wrong. */
export function buildDateSpec(
  form: DateForm
): { spec: DateSpec } | { problem: DateProblem } {
  const start = parseIsoDate(form.start);
  if (start === null) {
    return { problem: "start" };
  }
  const end = parseIsoDate(form.end);
  if (end === null) {
    return { problem: "end" };
  }
  if (end < start) {
    return { problem: "order" };
  }
  if (form.weekdays.length === 0) {
    return { problem: "weekdays" };
  }
  let fromMinute: number | null = null;
  let toMinute: number | null = null;
  if (form.useTime) {
    fromMinute = parseClock(form.from);
    toMinute = parseClock(form.to);
    if (fromMinute === null) {
      return { problem: "from" };
    }
    if (toMinute === null) {
      return { problem: "to" };
    }
    if (toMinute < fromMinute) {
      return { problem: "timeOrder" };
    }
  }
  const spec: DateSpec = {
    start,
    end,
    weekdays: form.weekdays,
    fromMinute,
    toMinute,
    stepMinutes: form.stepMinutes,
  };
  return countAllowedDays(spec) === 0 ? { problem: "empty" } : { spec };
}

/* --------------------------------------------------------------- formatting */

export type DateFormat = "iso" | "us" | "dayFirst" | "long" | "medium";
export const DATE_FORMATS: readonly DateFormat[] = [
  "iso",
  "long",
  "medium",
  "us",
  "dayFirst",
];

/**
 * One result as text.
 *
 * ISO 8601 and the two numeric forms are fixed layouts; `long` and `medium` use the
 * reader's locale through `Intl`, in UTC so the day never shifts. ISO always uses the
 * 24-hour clock; the other formats follow `hour12`.
 */
export function formatDrawn(
  drawn: DrawnDate,
  format: DateFormat,
  locale: string,
  hour12: boolean
): string {
  const { year, month, day } = civilFromDays(drawn.day);
  const hasTime = drawn.minute !== null;
  if (format === "iso") {
    const date = `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
    return hasTime ? `${date}T${toClock(drawn.minute ?? 0)}` : date;
  }
  const hourCycle = hour12 ? "h12" : "h23";
  if (format === "us" || format === "dayFirst") {
    const date =
      format === "us"
        ? `${pad(month, 2)}/${pad(day, 2)}/${pad(year, 4)}`
        : `${pad(day, 2)}/${pad(month, 2)}/${pad(year, 4)}`;
    if (!hasTime) {
      return date;
    }
    const time = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      hourCycle,
      timeZone: "UTC",
    }).format(toEpochMs(drawn));
    return `${date} ${time}`;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: format === "long" ? "full" : "medium",
    ...(hasTime ? { timeStyle: "short" as const, hourCycle } : {}),
    timeZone: "UTC",
  }).format(toEpochMs(drawn));
}

/** The weekday's name in the reader's language, e.g. "Mon" or "Monday". */
export function weekdayName(
  weekday: Weekday,
  locale: string,
  width: "short" | "long"
): string {
  // Day 3 since the epoch, 1970-01-04, was a Sunday.
  return new Intl.DateTimeFormat(locale, {
    weekday: width,
    timeZone: "UTC",
  }).format((3 + weekday) * MS_PER_DAY);
}
